/**
 * SearchFilters — EXL-672
 *
 * Sidebar/drawer component displaying all facet sections for search result
 * filtering.
 *
 * Facet sections (in priority order from facetConfig):
 *   1. Category   — multi-select checkboxes, up to 8 shown, "Show more" expands
 *   2. Type       — single-select radio buttons (mutually exclusive)
 *   3. Tags       — multi-select checkboxes with search, up to 10, "Show more"
 *                   loads 20 at a time (pagination, no virtualization)
 *   4. Status     — multi-select checkboxes
 *   5. Date Range — DateRangePicker component
 *
 * Zero-count options are visible but disabled (opacity-50, cursor-not-allowed).
 * Disabling is data-driven from server-returned facet counts.
 * Clicking a disabled option shows inline hint.
 */

import { useState, useMemo, useCallback } from 'react';
import { FACET_LABELS } from '../../config/facetConfig';
import { DateRangePicker } from './DateRangePicker';
import type { SearchFiltersState } from '../../hooks/useSearchFilters';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchFacets {
  category?: Record<string, number>;
  type?: Record<string, number>;
  tags?: Record<string, number>;
  status?: Record<string, number>;
  date_range?: Record<string, number>;
}

interface SearchFiltersProps {
  facets: SearchFacets | null;
  filters: SearchFiltersState;
  loading?: boolean;
  onCategoriesChange: (values: string[]) => void;
  onTypeChange: (value: string) => void;
  onTagsChange: (values: string[]) => void;
  onStatusesChange: (values: string[]) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

// ─── Type options ─────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: '',         label: 'All' },
  { value: 'articles', label: 'Articles' },
  { value: 'kb',       label: 'Knowledge Base' },
  { value: 'guides',   label: 'Guides' },
  { value: 'faqs',     label: 'FAQs' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface FacetSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FacetSection({ title, children, defaultOpen = true }: FacetSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const id = `facet-section-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="border-b border-neutral-200 last:border-0 py-3">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-controls={id}
        className="w-full flex items-center justify-between text-sm font-semibold
          text-neutral-800 hover:text-neutral-900 transition-colors"
      >
        <span>{title}</span>
        <svg
          className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div id={id} className="mt-2">
          {children}
        </div>
      )}
    </div>
  );
}

interface CheckboxOptionProps {
  label: string;
  count: number | undefined;
  checked: boolean;
  disabled: boolean;
  loading?: boolean;
  onChange: () => void;
  onDisabledClick?: () => void;
}

function CheckboxOption({
  label,
  count,
  checked,
  disabled,
  loading,
  onChange,
  onDisabledClick,
}: CheckboxOptionProps) {
  const [showHint, setShowHint] = useState(false);

  const handleClick = () => {
    if (disabled && !checked) {
      setShowHint(true);
      setTimeout(() => setShowHint(false), 2500);
      onDisabledClick?.();
      return;
    }
    onChange();
  };

  return (
    <div>
      <label
        className={`flex items-center gap-2 py-1 px-1 rounded cursor-pointer group
          hover:bg-neutral-50 transition-colors
          ${disabled && !checked ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={disabled && !checked ? 'No results match this option with current filters.' : undefined}
        onClick={disabled && !checked ? handleClick : undefined}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled && !checked}
          className="w-3.5 h-3.5 rounded border-neutral-400 text-neutral-900
            focus:ring-1 focus:ring-neutral-900/20 cursor-pointer"
          aria-label={`${label}${count != null ? ` (${count})` : ''}`}
        />
        <span className="flex-1 text-sm text-neutral-700 group-hover:text-neutral-900">
          {label}
        </span>
        <span className={`text-xs tabular-nums ${
          loading ? 'text-neutral-300' : 'text-neutral-400'
        }`}>
          {loading ? '--' : count != null ? `(${count})` : ''}
        </span>
      </label>
      {showHint && (
        <p className="text-xs text-neutral-500 px-6 pb-1">
          No results match this option with current filters.
        </p>
      )}
    </div>
  );
}

// ─── Tags facet with search + pagination ─────────────────────────────────────

interface TagsFacetProps {
  tagCounts: Record<string, number>;
  selected: string[];
  loading?: boolean;
  onChange: (values: string[]) => void;
}

const TAGS_INITIAL = 10;
const TAGS_PAGE_SIZE = 20;

function TagsFacet({ tagCounts, selected, loading, onChange }: TagsFacetProps) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(TAGS_INITIAL);

  // All tags sorted by count descending
  const allTags = useMemo(
    () =>
      Object.entries(tagCounts)
        .sort(([, a], [, b]) => b - a)
        .map(([value, count]) => ({ value, count })),
    [tagCounts]
  );

  // Filtered by search term
  const filtered = useMemo(() => {
    if (!search.trim()) return allTags;
    const q = search.toLowerCase();
    return allTags.filter((t) => t.value.toLowerCase().includes(q));
  }, [allTags, search]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {allTags.length > TAGS_INITIAL && (
        <input
          type="search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setVisibleCount(TAGS_INITIAL); }}
          placeholder="Search tags…"
          className="w-full px-2.5 py-1.5 text-xs border border-neutral-300 rounded
            focus:outline-none focus:ring-1 focus:ring-neutral-900/20 focus:border-neutral-500"
          aria-label="Search tags"
        />
      )}
      <div className="space-y-0.5">
        {visible.map(({ value, count }) => {
          const disabled = count === 0;
          return (
            <CheckboxOption
              key={value}
              label={value}
              count={count}
              checked={selected.includes(value)}
              disabled={disabled}
              loading={loading}
              onChange={() => toggle(value)}
            />
          );
        })}
        {allTags.length === 0 && !loading && (
          <p className="text-xs text-neutral-400">No tags available</p>
        )}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setVisibleCount((c) => c + TAGS_PAGE_SIZE)}
          className="text-xs text-neutral-500 hover:text-neutral-800 transition-colors underline"
        >
          Show more ({filtered.length - visibleCount} more)
        </button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function SearchFilters({
  facets,
  filters,
  loading,
  onCategoriesChange,
  onTypeChange,
  onTagsChange,
  onStatusesChange,
  onDateRangeChange,
  onClearAll,
  hasActiveFilters,
}: SearchFiltersProps) {
  const CATEGORY_INITIAL = 8;
  const [showAllCategories, setShowAllCategories] = useState(false);

  // Category options sorted by count descending, then alphabetical
  const categoryOptions = useMemo(() => {
    const counts = facets?.category ?? {};
    return Object.entries(counts).sort(([aKey, aCount], [bKey, bCount]) => {
      if (bCount !== aCount) return bCount - aCount;
      return aKey.localeCompare(bKey);
    });
  }, [facets?.category]);

  const visibleCategories = showAllCategories
    ? categoryOptions
    : categoryOptions.slice(0, CATEGORY_INITIAL);

  const toggleCategory = useCallback(
    (value: string) => {
      const next = filters.categories.includes(value)
        ? filters.categories.filter((v) => v !== value)
        : [...filters.categories, value];
      onCategoriesChange(next);
    },
    [filters.categories, onCategoriesChange]
  );

  // Status options sorted alphabetically
  const statusOptions = useMemo(() => {
    const counts = facets?.status ?? {};
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [facets?.status]);

  const toggleStatus = useCallback(
    (value: string) => {
      const next = filters.statuses.includes(value)
        ? filters.statuses.filter((v) => v !== value)
        : [...filters.statuses, value];
      onStatusesChange(next);
    },
    [filters.statuses, onStatusesChange]
  );

  const tagCounts = facets?.tags ?? {};
  const typeCounts = facets?.type ?? {};

  return (
    <aside
      className="w-full"
      aria-label="Search filters"
    >
      {/* ── Category ─────────────────────────────────────────────────────── */}
      <FacetSection title={FACET_LABELS.category}>
        {categoryOptions.length === 0 && !loading ? (
          <p className="text-xs text-neutral-400">No categories available</p>
        ) : (
          <div className="space-y-0.5">
            {visibleCategories.map(([value, count]) => {
              const disabled = count === 0;
              return (
                <CheckboxOption
                  key={value}
                  label={value}
                  count={count}
                  checked={filters.categories.includes(value)}
                  disabled={disabled}
                  loading={loading}
                  onChange={() => toggleCategory(value)}
                />
              );
            })}
          </div>
        )}
        {categoryOptions.length > CATEGORY_INITIAL && (
          <button
            type="button"
            onClick={() => setShowAllCategories((v) => !v)}
            className="mt-1 text-xs text-neutral-500 hover:text-neutral-800 transition-colors underline"
          >
            {showAllCategories
              ? 'Show less'
              : `Show more (${categoryOptions.length - CATEGORY_INITIAL} more)`}
          </button>
        )}
      </FacetSection>

      {/* ── Type ─────────────────────────────────────────────────────────── */}
      <FacetSection title={FACET_LABELS.type}>
        <div className="space-y-0.5">
          {TYPE_OPTIONS.map(({ value, label }) => {
            const count = value ? (typeCounts[value] ?? 0) : undefined;
            const disabled = value !== '' && count === 0 && !!facets;
            return (
              <label
                key={value}
                className={`flex items-center gap-2 py-1 px-1 rounded cursor-pointer group
                  hover:bg-neutral-50 transition-colors
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="search-type"
                  value={value}
                  checked={filters.type === value}
                  onChange={() => !disabled && onTypeChange(value)}
                  disabled={disabled}
                  className="w-3.5 h-3.5 border-neutral-400 text-neutral-900
                    focus:ring-1 focus:ring-neutral-900/20 cursor-pointer"
                />
                <span className="flex-1 text-sm text-neutral-700 group-hover:text-neutral-900">
                  {label}
                </span>
                {count != null && (
                  <span className={`text-xs tabular-nums ${loading ? 'text-neutral-300' : 'text-neutral-400'}`}>
                    {loading ? '--' : `(${count})`}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FacetSection>

      {/* ── Tags ─────────────────────────────────────────────────────────── */}
      <FacetSection title={FACET_LABELS.tags}>
        {Object.keys(tagCounts).length === 0 && !loading ? (
          <p className="text-xs text-neutral-400">No tags available</p>
        ) : (
          <TagsFacet
            tagCounts={tagCounts}
            selected={filters.tags}
            loading={loading}
            onChange={onTagsChange}
          />
        )}
      </FacetSection>

      {/* ── Status ───────────────────────────────────────────────────────── */}
      <FacetSection title={FACET_LABELS.status}>
        {statusOptions.length === 0 && !loading ? (
          <p className="text-xs text-neutral-400">No status options available</p>
        ) : (
          <div className="space-y-0.5">
            {statusOptions.map(([value, count]) => {
              const disabled = count === 0;
              return (
                <CheckboxOption
                  key={value}
                  label={value.charAt(0).toUpperCase() + value.slice(1)}
                  count={count}
                  checked={filters.statuses.includes(value)}
                  disabled={disabled}
                  loading={loading}
                  onChange={() => toggleStatus(value)}
                />
              );
            })}
          </div>
        )}
      </FacetSection>

      {/* ── Date Range ───────────────────────────────────────────────────── */}
      <FacetSection title={FACET_LABELS.published_date}>
        <DateRangePicker
          value={{ from: filters.dateFrom, to: filters.dateTo }}
          onChange={({ from, to }) => onDateRangeChange(from, to)}
        />
      </FacetSection>

      {/* ── Clear all ────────────────────────────────────────────────────── */}
      {hasActiveFilters && (
        <div className="pt-3">
          <button
            type="button"
            onClick={onClearAll}
            className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50
              border border-red-200 rounded hover:bg-red-100 transition-colors"
          >
            Clear all filters
          </button>
        </div>
      )}
    </aside>
  );
}

export default SearchFilters;
